import type { ApiClient } from "./client";

export type MeetingStatus =
  | "scheduled"
  | "in_progress"
  | "completed"
  | "failed"
  | "archived"
  | "no_transcript"
  | "too_short"
  | "summary_failed"
  | "summarized"
  | "recording";

export type MeetingLanguage = "id" | "en" | "bilingual";

export interface Meeting {
  id: string;
  title: string;
  meeting_url?: string;
  started_at?: string | null;
  ended_at?: string | null;
  owner_user_id?: string | null;
  status: MeetingStatus;
  language: MeetingLanguage;
  attendees: string[];
  created_at: string;
  deliveries?: DeliveryReceipt[];
}

export interface ActionItem {
  owner: string;
  task: string;
  due_date?: string;
}

export interface Topic {
  title: string;
  duration_minutes: number;
  notes?: string;
}

export interface SummaryContent {
  headline: string;
  decisions?: string[];
  action_items?: ActionItem[];
  notes?: string;
  topic_breakdown?: Topic[];
}

export interface Summary {
  id: string;
  language: MeetingLanguage;
  content: SummaryContent;
  model?: string;
  prompt_version?: string;
  transcript_trimmed: boolean;
  cost_usd?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  created_at: string;
}

export interface MeetingDetail extends Meeting {
  desktop_notes?: {
    raw_notes: string;
    enhanced_notes: string;
    version: number;
    capture_phase: string;
    generation_status: string;
  };
  summary?: Summary;
  deliveries: DeliveryReceipt[];
}

export interface CreateMeetingRequest {
  title: string;
  started_at?: string;
  ended_at?: string;
  status?: MeetingStatus;
  language?: MeetingLanguage;
  attendees?: string[];
  meeting_url?: string;
  do_not_record?: boolean;
}

export interface PutSummaryRequest {
  language?: MeetingLanguage;
  content: SummaryContent;
}

export type DeliveryChannel = "email" | "google_docs" | "slack";

export interface DeliveryReceipt {
  channel: DeliveryChannel;
  status: "ok" | "skipped" | "failed";
  error_reason?: string;
  created_at?: string;
}

export interface Attachment {
  id: string;
  meeting_id: string;
  meeting_title: string;
  kind: "recording" | "doc" | "pdf" | "image" | "other";
  url: string;
  mime_type?: string;
  byte_size?: number | null;
  created_at: string;
}

export const meetingsApi = (client: ApiClient) => ({
  list: (tenantId: string, opts?: { status?: MeetingStatus }) => {
    const q = opts?.status ? `?status=${encodeURIComponent(opts.status)}` : "";
    return client.get<{ meetings: Meeting[] }>(
      `/api/v1/tenants/${tenantId}/meetings${q}`,
    );
  },
  get: (tenantId: string, meetingId: string) =>
    client.get<MeetingDetail>(
      `/api/v1/tenants/${tenantId}/meetings/${meetingId}`,
    ),
  create: (tenantId: string, req: CreateMeetingRequest) =>
    client.post<Meeting>(`/api/v1/tenants/${tenantId}/meetings`, req),
  putSummary: (tenantId: string, meetingId: string, req: PutSummaryRequest) =>
    client.post<Summary>(
      `/api/v1/tenants/${tenantId}/meetings/${meetingId}/summary`,
      req,
    ),
  regenerateSummary: (tenantId: string, meetingId: string) =>
    client.post<{ status: string }>(
      `/api/v1/tenants/${tenantId}/meetings/${meetingId}/summary/regenerate`,
    ),
  /**
   * Re-run delivery. Pass a single channel to retry just that one, or omit
   * for all configured channels. Sends a fresh Idempotency-Key per call so
   * the server dedupes accidental double-clicks (PRO-36).
   */
  deliver: (
    tenantId: string,
    meetingId: string,
    channels: DeliveryChannel[] | { channel: DeliveryChannel },
  ) => {
    const base = `/api/v1/tenants/${tenantId}/meetings/${meetingId}/deliver`;
    const headers = { "Idempotency-Key": crypto.randomUUID() };
    if (!Array.isArray(channels)) {
      return client.request<{ receipts: DeliveryReceipt[] }>(
        `${base}?channel=${encodeURIComponent(channels.channel)}`,
        { method: "POST", headers },
      );
    }
    return client.request<{ receipts: DeliveryReceipt[] }>(base, {
      method: "POST",
      headers,
      body: JSON.stringify({ channels }),
    });
  },
  listAttachments: (tenantId: string, opts?: { kind?: Attachment["kind"] }) => {
    const q = opts?.kind ? `?kind=${encodeURIComponent(opts.kind)}` : "";
    return client.get<{ attachments: Attachment[] }>(
      `/api/v1/tenants/${tenantId}/attachments${q}`,
    );
  },
});
